import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTransportMutation, useTransportQueryUtils } from "@/transport"
import { toast } from "@coval/ui"
import { useQueryClient } from "@tanstack/react-query"

import { logger } from "@/lib/logger"
import type { DashboardLayoutResponse } from "@/types_api/backend/transport/dashboard_layout_response"
import type { ReplaceDashboardWidgetsResponse } from "@/types_api/backend/transport/replace_dashboard_widgets_response"

import type { DashboardMeta, DashboardWidget } from "../types/core"
import {
  mapTransportDashboardWidget,
  serializeDashboardMetadataForTransport,
  serializeGetDashboardLayoutRequest,
  serializeReplaceDashboardWidgetsRequest,
} from "../utils/transportDashboard"

const log = logger("DashboardWidgetSaveQueue")
const DASHBOARD_SCOPE_CHANGED_SAVE_ERROR_MESSAGE =
  "Dashboard scope changed before save completed"
const DASHBOARD_DELETED_SAVE_ERROR_MESSAGE =
  "Dashboard was deleted before save completed"

type SavedDashboardWidgetResource =
  ReplaceDashboardWidgetsResponse["widgets"][number]
type LayoutDashboardWidgetResource = DashboardLayoutResponse["widgets"][number]

export const toDashboardTransportError = (
  error: unknown,
  fallbackMessage: string,
): Error => (error instanceof Error ? error : new Error(fallbackMessage))

const createDashboardScopeChangedError = (): Error =>
  new Error(DASHBOARD_SCOPE_CHANGED_SAVE_ERROR_MESSAGE)

export const createDashboardDeletedSaveError = (): Error =>
  new Error(DASHBOARD_DELETED_SAVE_ERROR_MESSAGE)

interface QueuedDashboardSave {
  dashboardId: string
  scopeVersion: number
  widgets: DashboardWidget[]
  deferreds: Array<{
    resolve: () => void
    reject: (error: Error) => void
  }>
}

const rejectQueuedDashboardSave = (
  queuedSave: QueuedDashboardSave,
  error: Error,
) => {
  queuedSave.deferreds.forEach((deferred) => deferred.reject(error))
}

const mapSavedWidgetResourceForLayoutCache = (
  widget: SavedDashboardWidgetResource,
): LayoutDashboardWidgetResource => ({
  name: widget.name,
  display_name: widget.display_name,
  type: widget.type,
  grid_x: widget.grid_x,
  grid_y: widget.grid_y,
  grid_w: widget.grid_w,
  grid_h: widget.grid_h,
  config: widget.config,
  create_time: widget.create_time,
  update_time: widget.update_time,
})

export function useDashboardWidgetSaveQueue({
  baseWidgets,
  currentDashboardId,
  dashboardScope,
  dashboards,
  layoutData,
  setCurrentDashboardId,
}: {
  baseWidgets: DashboardWidget[]
  currentDashboardId: string | undefined
  dashboardScope: string | null
  dashboards: DashboardMeta[]
  layoutData: DashboardLayoutResponse | undefined
  setCurrentDashboardId: (dashboardId: string | undefined) => void
}) {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()
  const transportReplaceDashboardWidgetsMutation = useTransportMutation(
    "replaceDashboardWidgets",
  )
  const [isSavingDashboard, setIsSavingDashboard] = useState(false)
  const [transportWidgetOverrides, setTransportWidgetOverrides] = useState<
    Record<string, DashboardWidget[]>
  >({})
  const dashboardScopeRef = useRef<string | null>(null)
  const dashboardScopeVersionRef = useRef(0)
  const currentWidgetsRef = useRef<Record<string, DashboardWidget[]>>({})
  const confirmedWidgetsRef = useRef<Record<string, DashboardWidget[]>>({})
  const saveInFlightRef = useRef(false)
  const activeSaveRef = useRef<QueuedDashboardSave | null>(null)
  const pendingSaveRef = useRef<Map<string, QueuedDashboardSave>>(new Map())
  const cancelledDashboardSaveIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!dashboardScope) {
      return
    }

    if (dashboardScopeRef.current === null) {
      dashboardScopeRef.current = dashboardScope
      return
    }

    if (dashboardScopeRef.current === dashboardScope) {
      return
    }

    dashboardScopeRef.current = dashboardScope
    dashboardScopeVersionRef.current += 1

    const scopeChangedError = createDashboardScopeChangedError()
    if (activeSaveRef.current) {
      rejectQueuedDashboardSave(activeSaveRef.current, scopeChangedError)
    }
    pendingSaveRef.current.forEach((queuedSave) => {
      rejectQueuedDashboardSave(queuedSave, scopeChangedError)
    })

    setTransportWidgetOverrides({})
    currentWidgetsRef.current = {}
    confirmedWidgetsRef.current = {}
    pendingSaveRef.current = new Map()
    cancelledDashboardSaveIdsRef.current = new Set()
    activeSaveRef.current = null
    saveInFlightRef.current = false
    setIsSavingDashboard(false)
    setCurrentDashboardId(undefined)
  }, [dashboardScope, setCurrentDashboardId])

  const widgets = useMemo(() => {
    if (currentDashboardId && transportWidgetOverrides[currentDashboardId]) {
      return transportWidgetOverrides[currentDashboardId]
    }

    return baseWidgets
  }, [baseWidgets, currentDashboardId, transportWidgetOverrides])

  useEffect(() => {
    if (!currentDashboardId) {
      return
    }

    currentWidgetsRef.current[currentDashboardId] = widgets

    const hasPendingSave = pendingSaveRef.current.has(currentDashboardId)
    const hasActiveSave =
      activeSaveRef.current?.dashboardId === currentDashboardId

    if (!hasActiveSave && !hasPendingSave) {
      confirmedWidgetsRef.current[currentDashboardId] = widgets
    }
  }, [currentDashboardId, widgets])

  const syncSavingState = useCallback(() => {
    if (!currentDashboardId) {
      setIsSavingDashboard(false)
      return
    }

    setIsSavingDashboard(
      activeSaveRef.current?.dashboardId === currentDashboardId ||
        pendingSaveRef.current.has(currentDashboardId),
    )
  }, [currentDashboardId])

  useEffect(() => {
    syncSavingState()
  }, [syncSavingState])

  const setDashboardWidgetsCache = useCallback(
    (dashboardId: string, nextWidgets: DashboardWidget[]) => {
      currentWidgetsRef.current[dashboardId] = nextWidgets

      setTransportWidgetOverrides((previousOverrides) => ({
        ...previousOverrides,
        [dashboardId]: nextWidgets,
      }))
    },
    [],
  )

  const setDashboardLayoutQueryCache = useCallback(
    (
      dashboardId: string,
      savedWidgetResources: LayoutDashboardWidgetResource[],
    ) => {
      queryClient.setQueryData<DashboardLayoutResponse | undefined>(
        transportQueryUtils.getKey(
          "getDashboardLayout",
          serializeGetDashboardLayoutRequest(dashboardId),
        ),
        (existingData) => {
          if (existingData) {
            return {
              ...existingData,
              widgets: savedWidgetResources,
            }
          }

          const dashboard = dashboards.find((entry) => entry.id === dashboardId)
          if (!dashboard) {
            return existingData
          }

          return {
            dashboard: serializeDashboardMetadataForTransport(dashboard),
            widgets: savedWidgetResources,
          }
        },
      )
    },
    [dashboards, queryClient, transportQueryUtils],
  )

  const clearTransportWidgetOverride = useCallback((dashboardId: string) => {
    setTransportWidgetOverrides((previousOverrides) => {
      if (!(dashboardId in previousOverrides)) {
        return previousOverrides
      }

      const { [dashboardId]: _removed, ...rest } = previousOverrides
      return rest
    })
  }, [])

  const cancelDashboardSaves = useCallback(
    (dashboardId: string, error: Error) => {
      cancelledDashboardSaveIdsRef.current.add(dashboardId)

      if (activeSaveRef.current?.dashboardId === dashboardId) {
        rejectQueuedDashboardSave(activeSaveRef.current, error)
      }

      const pendingSave = pendingSaveRef.current.get(dashboardId)
      if (pendingSave) {
        rejectQueuedDashboardSave(pendingSave, error)
        pendingSaveRef.current.delete(dashboardId)
      }

      syncSavingState()
    },
    [syncSavingState],
  )

  useEffect(() => {
    if (!currentDashboardId || !layoutData) {
      return
    }

    const hasActiveSave =
      activeSaveRef.current?.dashboardId === currentDashboardId
    const hasPendingSave = pendingSaveRef.current.has(currentDashboardId)
    if (!hasActiveSave && !hasPendingSave) {
      clearTransportWidgetOverride(currentDashboardId)
    }
  }, [clearTransportWidgetOverride, currentDashboardId, layoutData])

  const dequeueNextSave = useCallback((): QueuedDashboardSave | null => {
    const nextPendingSave = pendingSaveRef.current.entries().next()
    if (nextPendingSave.done) {
      return null
    }

    const [dashboardId, queuedSave] = nextPendingSave.value
    pendingSaveRef.current.delete(dashboardId)
    return queuedSave
  }, [])

  const processDashboardSave = useCallback(
    async (initialSave: QueuedDashboardSave) => {
      let nextSave: QueuedDashboardSave | null = initialSave
      const scopeVersion = initialSave.scopeVersion
      const isCurrentScope = () =>
        dashboardScopeVersionRef.current === scopeVersion

      while (nextSave && isCurrentScope()) {
        const activeSave = nextSave
        if (activeSave.scopeVersion !== scopeVersion) {
          rejectQueuedDashboardSave(
            activeSave,
            createDashboardScopeChangedError(),
          )
          nextSave = dequeueNextSave()
          continue
        }

        if (cancelledDashboardSaveIdsRef.current.has(activeSave.dashboardId)) {
          rejectQueuedDashboardSave(
            activeSave,
            createDashboardDeletedSaveError(),
          )
          nextSave = dequeueNextSave()
          continue
        }

        activeSaveRef.current = activeSave
        syncSavingState()

        try {
          const replaceResponse =
            await transportReplaceDashboardWidgetsMutation.mutateAsync(
              serializeReplaceDashboardWidgetsRequest(
                activeSave.dashboardId,
                activeSave.widgets,
              ),
            )
          const savedWidgetResources = replaceResponse.widgets.map(
            mapSavedWidgetResourceForLayoutCache,
          )
          const savedWidgets = savedWidgetResources.map((widget) =>
            mapTransportDashboardWidget(widget),
          )

          if (!isCurrentScope()) {
            return
          }

          if (
            cancelledDashboardSaveIdsRef.current.has(activeSave.dashboardId)
          ) {
            nextSave = dequeueNextSave()
            continue
          }

          confirmedWidgetsRef.current[activeSave.dashboardId] = savedWidgets
          activeSave.deferreds.forEach((deferred) => deferred.resolve())
          if (!pendingSaveRef.current.has(activeSave.dashboardId)) {
            setDashboardLayoutQueryCache(
              activeSave.dashboardId,
              savedWidgetResources,
            )
            setDashboardWidgetsCache(activeSave.dashboardId, savedWidgets)
          }

          if (!isCurrentScope()) {
            return
          }

          nextSave = dequeueNextSave()
        } catch (error) {
          if (!isCurrentScope()) {
            return
          }

          if (
            cancelledDashboardSaveIdsRef.current.has(activeSave.dashboardId)
          ) {
            nextSave = dequeueNextSave()
            continue
          }

          const normalizedError = toDashboardTransportError(
            error,
            "Failed to save dashboard changes",
          )

          activeSave.deferreds.forEach((deferred) =>
            deferred.reject(normalizedError),
          )

          const stalePendingSave = pendingSaveRef.current.get(
            activeSave.dashboardId,
          )
          pendingSaveRef.current.delete(activeSave.dashboardId)
          stalePendingSave?.deferreds.forEach((deferred) =>
            deferred.reject(normalizedError),
          )

          setDashboardWidgetsCache(
            activeSave.dashboardId,
            confirmedWidgetsRef.current[activeSave.dashboardId] ?? [],
          )

          toast.danger({
            title: "Failed to save changes",
            description:
              "Your dashboard changes couldn't be saved. Please try again.",
          })

          log.error("Failed to save dashboard", normalizedError, {
            dashboardId: activeSave.dashboardId,
          })

          nextSave = dequeueNextSave()
        }
      }

      if (!isCurrentScope()) {
        return
      }

      activeSaveRef.current = null
      saveInFlightRef.current = false
      syncSavingState()
    },
    [
      dequeueNextSave,
      setDashboardLayoutQueryCache,
      setDashboardWidgetsCache,
      syncSavingState,
      transportReplaceDashboardWidgetsMutation,
    ],
  )

  const enqueueDashboardSave = useCallback(
    async (dashboardId: string, nextWidgets: DashboardWidget[]) => {
      const scopeVersion = dashboardScopeVersionRef.current
      await queryClient.cancelQueries({
        queryKey: transportQueryUtils.getKey(
          "getDashboardLayout",
          serializeGetDashboardLayoutRequest(dashboardId),
        ),
      })

      if (dashboardScopeVersionRef.current !== scopeVersion) {
        throw createDashboardScopeChangedError()
      }

      const completion = new Promise<void>((resolve, reject) => {
        const deferred = { resolve, reject }

        if (saveInFlightRef.current) {
          const pendingSave = pendingSaveRef.current.get(dashboardId)
          pendingSaveRef.current.set(dashboardId, {
            dashboardId,
            scopeVersion,
            widgets: nextWidgets,
            deferreds: pendingSave
              ? [...pendingSave.deferreds, deferred]
              : [deferred],
          })
        } else {
          const initialSave = {
            dashboardId,
            scopeVersion,
            widgets: nextWidgets,
            deferreds: [deferred],
          }
          saveInFlightRef.current = true
          activeSaveRef.current = initialSave
          void processDashboardSave(initialSave)
        }
      })

      setDashboardWidgetsCache(dashboardId, nextWidgets)
      syncSavingState()

      return completion
    },
    [
      processDashboardSave,
      queryClient,
      setDashboardWidgetsCache,
      syncSavingState,
      transportQueryUtils,
    ],
  )

  const safeEnqueueDashboardSave = useCallback(
    (dashboardId: string, nextWidgets: DashboardWidget[]) => {
      void enqueueDashboardSave(dashboardId, nextWidgets).catch(() => undefined)
    },
    [enqueueDashboardSave],
  )

  return {
    cancelDashboardSaves,
    clearTransportWidgetOverride,
    currentWidgetsRef,
    enqueueDashboardSave,
    isSavingDashboard,
    safeEnqueueDashboardSave,
    widgets,
  }
}
